import React, { useState, useEffect, useRef } from 'react';
import { XMarkIcon, CalendarIcon, ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import '../styles/custom-date-range-picker.css';

interface CustomDateRangePickerProps {
  isOpen: boolean;
  onClose: () => void;
  onApply: (startDate: string, endDate: string) => void;
  initialStartDate?: string;
  initialEndDate?: string;
}

export default function CustomDateRangePicker({
  isOpen,
  onClose,
  onApply,
  initialStartDate,
  initialEndDate
}: CustomDateRangePickerProps) {
  const [startDate, setStartDate] = useState(initialStartDate || '');
  const [endDate, setEndDate] = useState(initialEndDate || '');
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectingStart, setSelectingStart] = useState(true);
  const modalRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (initialStartDate) setStartDate(initialStartDate);
    if (initialEndDate) setEndDate(initialEndDate);
  }, [initialStartDate, initialEndDate]);

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen, onClose]);

  // Preset date ranges
  const presets = [
    { label: 'Today', getValue: () => {
      const today = formatDate(new Date());
      return { start: today, end: today };
    }},
    { label: 'Yesterday', getValue: () => {
      const yesterday = new Date();
      yesterday.setDate(yesterday.getDate() - 1);
      const date = formatDate(yesterday);
      return { start: date, end: date };
    }},
    { label: 'Last 7 Days', getValue: () => {
      const end = new Date();
      const start = new Date();
      start.setDate(start.getDate() - 6);
      return { start: formatDate(start), end: formatDate(end) };
    }},
    { label: 'Last 30 Days', getValue: () => {
      const end = new Date();
      const start = new Date();
      start.setDate(start.getDate() - 29);
      return { start: formatDate(start), end: formatDate(end) };
    }},
    { label: 'This Month', getValue: () => {
      const now = new Date();
      const start = new Date(now.getFullYear(), now.getMonth(), 1);
      const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
      return { start: formatDate(start), end: formatDate(end) };
    }},
    { label: 'Last Month', getValue: () => {
      const now = new Date();
      const start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const end = new Date(now.getFullYear(), now.getMonth(), 0);
      return { start: formatDate(start), end: formatDate(end) };
    }}
  ];

  const formatDate = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const parseDate = (dateStr: string): Date | null => {
    if (!dateStr) return null;
    const date = new Date(dateStr);
    return isNaN(date.getTime()) ? null : date;
  };

  const handlePresetClick = (preset: typeof presets[0]) => {
    const { start, end } = preset.getValue();
    setStartDate(start);
    setEndDate(end);
  };

  const handleApply = () => {
    if (startDate && endDate) {
      const start = parseDate(startDate);
      const end = parseDate(endDate);

      if (start && end && start <= end) {
        onApply(startDate, endDate);
        onClose();
      } else {
        alert('Invalid date range. Start date must be before or equal to end date.');
      }
    } else {
      alert('Please select both start and end dates.');
    }
  };

  const handleDateClick = (date: Date) => {
    const dateStr = formatDate(date);

    if (selectingStart) {
      setStartDate(dateStr);
      setSelectingStart(false);
      // If end date is before start date, clear it
      if (endDate && parseDate(endDate)! < date) {
        setEndDate('');
      }
    } else {
      // If selecting end and clicked date is before start, swap them
      if (startDate && parseDate(dateStr)! < parseDate(startDate)!) {
        setEndDate(startDate);
        setStartDate(dateStr);
      } else {
        setEndDate(dateStr);
      }
      setSelectingStart(true);
    }
  };

  const renderCalendar = () => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days = [];
    const today = new Date();
    const startDateObj = startDate ? parseDate(startDate) : null;
    const endDateObj = endDate ? parseDate(endDate) : null;

    // Add empty cells for days before the first day of the month
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(<div key={`empty-${i}`} className="calendar-day empty"></div>);
    }

    // Add days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(year, month, day);
      const dateStr = formatDate(date);
      const isToday = date.toDateString() === today.toDateString();
      const isStart = startDateObj && date.toDateString() === startDateObj.toDateString();
      const isEnd = endDateObj && date.toDateString() === endDateObj.toDateString();
      const isInRange = startDateObj && endDateObj && date >= startDateObj && date <= endDateObj;
      const isFuture = date > today;

      days.push(
        <button
          key={day}
          type="button"
          className={`calendar-day ${isToday ? 'today' : ''} ${isStart ? 'start' : ''} ${isEnd ? 'end' : ''} ${isInRange ? 'in-range' : ''} ${isFuture ? 'future' : ''}`}
          onClick={() => !isFuture && handleDateClick(date)}
          disabled={isFuture}
        >
          {day}
        </button>
      );
    }

    return days;
  };

  const goToPreviousMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1));
  };

  const goToNextMonth = () => {
    const nextMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1);
    const today = new Date();
    // Don't allow going beyond current month
    if (nextMonth.getFullYear() < today.getFullYear() ||
        (nextMonth.getFullYear() === today.getFullYear() && nextMonth.getMonth() <= today.getMonth())) {
      setCurrentMonth(nextMonth);
    }
  };

  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'];

  const canGoNext = () => {
    const today = new Date();
    return currentMonth.getFullYear() < today.getFullYear() ||
           (currentMonth.getFullYear() === today.getFullYear() && currentMonth.getMonth() < today.getMonth());
  };

  if (!isOpen) return null;

  return (
    <div className="date-picker-overlay">
      <div className="date-picker-modal" ref={modalRef}>
        {/* Header */}
        <div className="date-picker-header">
          <div className="header-title">
            <CalendarIcon className="icon" />
            <h2>Select Date Range</h2>
          </div>
          <button type="button" className="close-button" onClick={onClose}>
            <XMarkIcon className="icon" />
          </button>
        </div>

        {/* Content */}
        <div className="date-picker-content">
          {/* Preset Buttons - Side Panel on Desktop, Top on Mobile */}
          <div className="presets-panel">
            <h3>Quick Select</h3>
            <div className="presets-list">
              {presets.map((preset) => (
                <button
                  key={preset.label}
                  type="button"
                  className="preset-button"
                  onClick={() => handlePresetClick(preset)}
                >
                  {preset.label}
                </button>
              ))}
            </div>
          </div>

          {/* Calendar Section */}
          <div className="calendar-section">
            {/* Selected Dates Display */}
            <div className="selected-dates">
              <div className="date-input-group">
                <label>Start Date</label>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  max={formatDate(new Date())}
                  className="date-input"
                />
              </div>
              <div className="date-separator">→</div>
              <div className="date-input-group">
                <label>End Date</label>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  min={startDate}
                  max={formatDate(new Date())}
                  className="date-input"
                />
              </div>
            </div>

            {/* Calendar */}
            <div className="calendar">
              <div className="calendar-header">
                <button type="button" className="nav-button" onClick={goToPreviousMonth}>
                  <ChevronLeftIcon className="icon" />
                </button>
                <div className="month-year">
                  {monthNames[currentMonth.getMonth()]} {currentMonth.getFullYear()}
                </div>
                <button
                  type="button"
                  className="nav-button"
                  onClick={goToNextMonth}
                  disabled={!canGoNext()}
                >
                  <ChevronRightIcon className="icon" />
                </button>
              </div>

              <div className="calendar-weekdays">
                <div className="weekday">Sun</div>
                <div className="weekday">Mon</div>
                <div className="weekday">Tue</div>
                <div className="weekday">Wed</div>
                <div className="weekday">Thu</div>
                <div className="weekday">Fri</div>
                <div className="weekday">Sat</div>
              </div>

              <div className="calendar-grid">
                {renderCalendar()}
              </div>

              {/* Selection Hint */}
              <div className="selection-hint">
                {selectingStart ? (
                  <span>📅 Select start date</span>
                ) : (
                  <span>📅 Select end date</span>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="date-picker-footer">
          <button type="button" className="cancel-button" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="apply-button"
            onClick={handleApply}
            disabled={!startDate || !endDate}
          >
            Apply Filter
          </button>
        </div>
      </div>
    </div>
  );
}
